import React, { useState, useEffect, Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";
import { AuthProvider } from "./context/AuthContext";
import { Toaster } from "sonner";
import TawkToChat from "./components/TawkToChat";
import GoogleAnalytics from "./components/GoogleAnalytics";
import DynamicFavicon from "./components/DynamicFavicon";
import { usePageTracking } from "./hooks/useAnalytics";

// Google Analytics Measurement ID - Configure in environment or here
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || null;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Critical page - loaded immediately
import HomePage from "./pages/HomePage";

// Lazy loaded pages - loaded on demand for better performance
const EquipmentPage = lazy(() => import("./pages/EquipmentPage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const ReferencesPage = lazy(() => import("./pages/ReferencesPage"));
const PartnersPage = lazy(() => import("./pages/PartnersPage"));
const PartnerDetailPage = lazy(() => import("./pages/PartnerDetailPage"));
const BecomePartnerPage = lazy(() => import("./pages/BecomePartnerPage"));
const ClientFeedbackPage = lazy(() => import("./pages/ClientFeedbackPage"));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const UnauthorizedPage = lazy(() => import("./pages/UnauthorizedPage"));
const MaintenancePage = lazy(() => import("./pages/MaintenancePage"));
const SetupRestore = lazy(() => import("./pages/SetupRestore"));

// Components
import AuthCallback from "./components/AuthCallback";
import ProtectedRoute from "./components/ProtectedRoute";

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-slate-500 text-sm">Chargement...</p>
    </div>
  </div>
);

// Analytics tracking wrapper
function AnalyticsTracker({ children }) {
  usePageTracking();
  return children;
}

// Maintenance mode checker
function MaintenanceChecker({ children }) {
  const [maintenance, setMaintenance] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    // Don't check maintenance for admin pages, login, or setup
    if (location.pathname.startsWith('/lina') || 
        location.pathname.startsWith('/login') || 
        location.pathname.startsWith('/setup')) {
      setLoading(false);
      return;
    }

    const checkMaintenance = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/maintenance-status`);
        if (response.ok) {
          const data = await response.json();
          setMaintenance(data);
        }
      } catch (err) {
        // If can't check, assume site is available
        setMaintenance({ enabled: false });
      } finally {
        setLoading(false);
      }
    };

    checkMaintenance();
  }, [location.pathname]);

  if (loading) {
    return null; // Or a loading spinner
  }

  // Show maintenance page for public routes when enabled
  if (maintenance?.enabled && 
      !location.pathname.startsWith('/lina') && 
      !location.pathname.startsWith('/login') &&
      !location.pathname.startsWith('/setup')) {
    return <MaintenancePage settings={maintenance} />;
  }

  return children;
}

// Router component that handles auth callback detection
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment (not query params) for session_id - detect SYNCHRONOUSLY during render
  // This prevents race conditions by processing new session_id FIRST before checking existing session_token
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/equipment" element={<EquipmentPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/references" element={<ReferencesPage />} />
        <Route path="/partners" element={<PartnersPage />} />
        <Route path="/partner/:id" element={<PartnerDetailPage />} />
        <Route path="/become-partner" element={<BecomePartnerPage />} />
        <Route path="/client-feedback" element={<ClientFeedbackPage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
        <Route path="/setup" element={<SetupRestore />} />
        <Route 
          path="/lina" 
          element={
            <ProtectedRoute requireAdmin>
              <AdminPage />
            </ProtectedRoute>
          } 
        />
        {/* /admin désactivé - redirige vers 404 */}
        <Route path="/admin" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
          <div className="App">
            <DynamicFavicon />
            <GoogleAnalytics measurementId={GA_MEASUREMENT_ID} />
            <MaintenanceChecker>
              <AnalyticsTracker>
                <AppRouter />
              </AnalyticsTracker>
            </MaintenanceChecker>
            <Toaster position="top-right" richColors />
            <TawkToChat />
          </div>
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
